import * as THREE from "three";
import type { Block, Blocks } from "../blocks";
import type { Shape } from "../physics/materials";
import type { CameraRig } from "../render/camera";
import type { Material } from "../render/materials";
import { el } from "../ui/dom";
import { alignYaw, yawOf } from "./snap";
import { surfaceTop } from "./surface";

/* =====================================================================
   PLACE TOOL: GHOST PREVIEW
   The ghost floats above the tower; a raycast from the pointer onto a
   horizontal plane decides where it sits.
   ===================================================================== */
const MAX_DROP = 50;
const AIM_LIMIT = 13.5;

export type PlaceTool = ReturnType<typeof createPlaceTool>;

export function createPlaceTool(
  scene: THREE.Scene,
  blocks: Blocks,
  rig: CameraRig,
  camera: THREE.Camera,
  material: Material,
  shape: Shape,
) {
  let currentMat = material;
  let currentShape = shape;
  let yaw = 0;

  const ghost = new THREE.Mesh(new THREE.BoxGeometry(...currentShape.size), currentMat.ghost);
  scene.add(ghost);
  const guideGeo = new THREE.BufferGeometry().setFromPoints([
    new THREE.Vector3(),
    new THREE.Vector3(),
  ]);
  const guide = new THREE.Line(
    guideGeo,
    new THREE.LineBasicMaterial({ color: 0xffffff, transparent: true, opacity: 0.6 }),
  );
  scene.add(guide);

  /* ---------- Drop height: the gap between the block and the surface below ---------- */
  let clearance = 0;
  const dropInput = el<HTMLInputElement>("dropH");
  const dropLabel = el("dropLabel");
  // The slider is curved (value grows with the square of its position), so the
  // low end still allows fine control while the top reaches 50 m
  function setClearance(v: number) {
    clearance = Math.round(THREE.MathUtils.clamp(v, 0, MAX_DROP) * 100) / 100;
    dropInput.value = String(Math.round(Math.sqrt(clearance / MAX_DROP) * 100));
    dropLabel.textContent =
      clearance < 0.05
        ? "Place gently"
        : clearance < 10
          ? clearance.toFixed(1) + " m"
          : Math.round(clearance) + " m";
  }
  // Keyboard and scroll steps grow with the height: small near the ground, big up high
  function clearanceStep() {
    return Math.max(0.1, clearance * 0.12);
  }
  dropInput.addEventListener("input", () =>
    setClearance(MAX_DROP * (Number(dropInput.value) / 100) ** 2),
  );
  setClearance(0);

  // Footprint outline: shows exactly where the block will land
  const footprint = new THREE.LineLoop(
    new THREE.BufferGeometry(),
    new THREE.LineBasicMaterial({ color: 0xffffff, transparent: true, opacity: 0.9 }),
  );
  scene.add(footprint);
  function updateFootprint() {
    const [w, , d] = currentShape.size;
    footprint.geometry.dispose();
    footprint.geometry = new THREE.BufferGeometry().setFromPoints([
      new THREE.Vector3(-w / 2, 0, -d / 2),
      new THREE.Vector3(w / 2, 0, -d / 2),
      new THREE.Vector3(w / 2, 0, d / 2),
      new THREE.Vector3(-w / 2, 0, d / 2),
    ]);
  }
  updateFootprint();

  const aim = new THREE.Vector3(0, 0, 0);
  let dropY = 2;
  let aimPlaneY = 0;
  let targetDrop = 2;
  let lastDrop = -1;

  // Finds the highest surface under the block's footprint. The block is then
  // held 'clearance' metres above it.
  function surfaceBelow(includeMoving = false) {
    const [w, , d] = currentShape.size;
    const c = Math.abs(Math.cos(yaw)),
      s = Math.abs(Math.sin(yaw));
    const hx = (c * w) / 2 + (s * d) / 2,
      hz = (s * w) / 2 + (c * d) / 2;
    return surfaceTop(
      blocks.list,
      aim.x - hx,
      aim.x + hx,
      aim.z - hz,
      aim.z + hz,
      (b) => includeMoving || !!b.landed, // ignore blocks still falling
    );
  }

  /** Where a screen point meets the plane the preview is aimed across. */
  function planePoint(x: number, y: number) {
    return rig.screenToPlane(x, y, aimPlaneY);
  }
  function setAim(x: number, z: number) {
    aim.x = THREE.MathUtils.clamp(x, -AIM_LIMIT, AIM_LIMIT);
    aim.z = THREE.MathUtils.clamp(z, -AIM_LIMIT, AIM_LIMIT);
  }

  return {
    aim,
    planePoint,
    setAim,
    clearance: () => clearance,
    /** Raises (dir > 0) or lowers the drop height by one step. */
    nudgeClearance(dir: number) {
      setClearance(clearance + dir * clearanceStep());
    },
    /**
     * Aims at what is under a screen point: the spot on a block or on the
     * ground there. With `snap` (a tap on a touchscreen), a block means "on top
     * of this one": the preview centres on it and lines up with its edges.
     * The mouse aims at the exact spot, so a block can still overhang another.
     */
    aimAt(x: number, y: number, snap = false) {
      const hit = rig.raycast(x, y).intersectObjects(
        blocks.list.map((b) => b.mesh),
        false,
      )[0];
      if (hit && snap) {
        const b = hit.object.userData.block as Block;
        setAim(b.pos.x, b.pos.z);
        yaw = alignYaw(yaw, yawOf(b.quat));
        return;
      }
      const h = hit ? hit.point : rig.screenToPlane(x, y, 0);
      if (h) setAim(h.x, h.z);
    },
    nearGhost(x: number, y: number) {
      if (!ghost.visible) return false;
      const v = ghost.position.clone().project(camera);
      return Math.hypot(((v.x + 1) / 2) * innerWidth - x, ((1 - v.y) / 2) * innerHeight - y) < 60;
    },
    setMaterial(m: Material) {
      currentMat = m;
      ghost.material = currentMat.ghost;
    },
    setShape(s: Shape) {
      currentShape = s;
      ghost.geometry.dispose();
      ghost.geometry = new THREE.BoxGeometry(...currentShape.size);
      updateFootprint();
    },
    turn(dir: number) {
      yaw += (dir * Math.PI) / 4;
    },
    setVisible(on: boolean) {
      ghost.visible = guide.visible = on;
    },

    drop(simTime: number) {
      if (simTime - lastDrop < 0.25) return; // small cooldown
      lastDrop = simTime;
      // Never spawn below the computed safe height, even if the ghost is still easing into place
      // The ghost ignores falling blocks, but the spawn point must clear them
      const s = surfaceBelow(true);
      const safe = (s === -Infinity ? 0 : s) + currentShape.size[1] / 2 + Math.max(clearance, 0.03);
      blocks.spawn(
        currentMat,
        currentShape,
        new THREE.Vector3(aim.x, Math.max(dropY, targetDrop, safe), aim.z),
        yaw,
        simTime,
      );
    },

    // Ghost hovers 'clearance' metres above whatever is directly beneath it
    update(dt: number, localTop: number, placing: boolean) {
      aimPlaneY = Math.max(0, localTop);
      const surface = surfaceBelow();
      const overVoid = surface === -Infinity;
      const base = overVoid ? 0 : surface; // over the void, hover at pedestal level
      targetDrop = base + currentShape.size[1] / 2 + Math.max(clearance, 0.03);
      dropY += (targetDrop - dropY) * Math.min(1, dt * 8);
      footprint.visible = !overVoid && placing;
      footprint.position.set(aim.x, base + 0.02, aim.z);
      footprint.rotation.y = yaw;
      ghost.position.set(aim.x, dropY, aim.z);
      ghost.rotation.set(0, yaw, 0);
      const gp = guideGeo.attributes.position;
      gp.setXYZ(0, aim.x, dropY - currentShape.size[1] / 2, aim.z);
      gp.setXYZ(1, aim.x, overVoid ? -12 : surface, aim.z);
      gp.needsUpdate = true;
    },
  };
}
